import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../lib/utils';

export type StudentDateOfBirthValue = {
  month: string;
  year: string;
  day: string;
};

export type StudentDateOfBirthFieldErrors = {
  month?: string;
  year?: string;
};

export function validateStudentDateOfBirthRequired(
  value: StudentDateOfBirthValue
): StudentDateOfBirthFieldErrors {
  const errors: StudentDateOfBirthFieldErrors = {};
  if (!value.month) {
    errors.month = 'Month is required.';
  }
  if (!value.year) {
    errors.year = 'Year is required.';
  }
  return errors;
}

export function scrollToFirstStudentDateOfBirthError(
  idPrefix: string,
  errors: StudentDateOfBirthFieldErrors
) {
  const firstErrorId = errors.month
    ? `${idPrefix}-month`
    : errors.year
      ? `${idPrefix}-year`
      : null;

  if (!firstErrorId) return;

  requestAnimationFrame(() => {
    const element = document.getElementById(firstErrorId);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (element instanceof HTMLElement) {
      element.focus({ preventScroll: true });
    }
  });
}

const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 31 }, (_, index) => String(currentYear - index));

export const emptyStudentDateOfBirth = (): StudentDateOfBirthValue => ({
  month: '',
  year: '',
  day: '',
});

export function studentDateOfBirthFromLegacyIso(isoDate: string): StudentDateOfBirthValue {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return emptyStudentDateOfBirth();
  }

  return {
    month: String(date.getUTCMonth() + 1),
    year: String(date.getUTCFullYear()),
    day: String(date.getUTCDate()),
  };
}

export function studentDateOfBirthFromFormValue(value: unknown, messageNumber = '161'): StudentDateOfBirthValue {
  if (!value || typeof value !== 'object') {
    return emptyStudentDateOfBirth();
  }

  const record = value as Record<string, unknown>;
  const yearKey = `c${messageNumber}_Year`;
  const monthKey = `c${messageNumber}_Month`;
  const dayKey = `c${messageNumber}_Day`;
  const month =
    record[`C${messageNumber}_Month`] ??
    record[monthKey] ??
    record.Month ??
    record.month;
  const year =
    record[`C${messageNumber}_Year`] ??
    record[yearKey] ??
    record.Year ??
    record.year;
  const day =
    record[`C${messageNumber}_Day`] ??
    record[dayKey] ??
    record.Day ??
    record.day;

  return {
    month: month != null && month !== '' ? String(month) : '',
    year: year != null && year !== '' ? String(year) : '',
    day: day != null && day !== '' ? String(day) : '',
  };
}

type FormFieldData = {
  name?: string;
  initialValue?: unknown;
};

/** SOAP form data flattens nested message types (e.g. c161_DateOfBirth.c161_Year). */
export function studentDateOfBirthFromFormFieldData(
  fieldData: FormFieldData[],
  messageNumber = '161'
): StudentDateOfBirthValue {
  const flatPrefix = `c${messageNumber}_DateOfBirth.c${messageNumber}_`;
  const flatYear = fieldData.find((field) => field.name === `${flatPrefix}Year`);
  const flatMonth = fieldData.find((field) => field.name === `${flatPrefix}Month`);
  const flatDay = fieldData.find((field) => field.name === `${flatPrefix}Day`);

  if (flatYear?.initialValue != null || flatMonth?.initialValue != null) {
    return {
      year:
        flatYear?.initialValue != null && flatYear.initialValue !== ''
          ? String(flatYear.initialValue)
          : '',
      month:
        flatMonth?.initialValue != null && flatMonth.initialValue !== ''
          ? String(flatMonth.initialValue)
          : '',
      day:
        flatDay?.initialValue != null && flatDay.initialValue !== ''
          ? String(flatDay.initialValue)
          : '',
    };
  }

  const nestedField = fieldData.find((field) => field.name === `c${messageNumber}_DateOfBirth`);
  if (nestedField?.initialValue) {
    return studentDateOfBirthFromFormValue(nestedField.initialValue, messageNumber);
  }

  return emptyStudentDateOfBirth();
}

export function toAddStudentDateOfBirthCommand(value: StudentDateOfBirthValue) {
  return {
    C159_Year: value.year ? parseInt(value.year, 10) : null,
    C159_Month: value.month ? parseInt(value.month, 10) : null,
    C159_Day: value.day ? parseInt(value.day, 10) : null,
  };
}

export function toEditStudentDateOfBirthCommand(value: StudentDateOfBirthValue) {
  return {
    C161_Year: value.year ? parseInt(value.year, 10) : null,
    C161_Month: value.month ? parseInt(value.month, 10) : null,
    C161_Day: value.day ? parseInt(value.day, 10) : null,
  };
}

type StudentDateOfBirthFieldsProps = {
  value: StudentDateOfBirthValue;
  onChange: (value: StudentDateOfBirthValue) => void;
  idPrefix?: string;
  errors?: StudentDateOfBirthFieldErrors;
  onMonthBlur?: () => void;
  onYearBlur?: () => void;
};

export function StudentDateOfBirthFields({
  value,
  onChange,
  idPrefix = 'dob',
  errors = {},
  onMonthBlur,
  onYearBlur,
}: StudentDateOfBirthFieldsProps) {
  return (
    <div className="space-y-2">
      <Label>Date of birth</Label>
      <div className="text-sm text-gray-600 mb-2 space-y-2">
        <p>
          We use your child&apos;s age to tailor lessons, readings, and activities — month and year are required
          because we cannot create relevant content without knowing their approximate age.
        </p>
        <p>
          The day is optional. If you add it, we can offer birthday-specific features when those are available.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-month`}>Month</Label>
          <Select
            value={value.month}
            onValueChange={(month) => onChange({ ...value, month })}
          >
            <SelectTrigger
              id={`${idPrefix}-month`}
              className={cn(errors.month && 'border-destructive-action')}
              onBlur={onMonthBlur}
            >
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((month) => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.month && (
            <p className="text-sm text-destructive-action">{errors.month}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-year`}>Year</Label>
          <Select
            value={value.year}
            onValueChange={(year) => onChange({ ...value, year })}
          >
            <SelectTrigger
              id={`${idPrefix}-year`}
              className={cn(errors.year && 'border-destructive-action')}
              onBlur={onYearBlur}
            >
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((year) => (
                <SelectItem key={year} value={year}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.year && (
            <p className="text-sm text-destructive-action">{errors.year}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-day`}>Day (optional)</Label>
          <Input
            id={`${idPrefix}-day`}
            type="number"
            min={1}
            max={31}
            placeholder="Optional"
            value={value.day}
            onChange={(e) => onChange({ ...value, day: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}
